import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import CInput from "../../../components/CInput";
import ProjectImageUpload from "./ProjectImageUpload";
import CustomerSelect from "./CustomerSelect";
import CreateCustomerModal from "./CreateCustomerModal";
import LocationSearch from "./LocationSearch";
import {
  formatIntegerAmount,
  parseIntegerAmount,
} from "../../../utils/CommonFunction";

const SectionHeader = ({ title }) => (
  <Typography
    sx={{
      fontSize: 16,
      fontWeight: 600,
      color: "#0D0D0E",
      mb: 1.5,
      fontFamily: "Inter Tight",
    }}
  >
    {title}
  </Typography>
);

const TIMELINE_KEYS = [
  "Approvals",
  "Demolition & Site Prep",
  "Construction",
  "Finishing & Handover",
];

const TIMELINE_PLACEHOLDERS = {
  Approvals: "e.g., 2",
  "Demolition & Site Prep": "e.g., 1",
  Construction: "e.g., 8",
  "Finishing & Handover": "e.g., 2",
};

const requiredLabel = (text) => (
  <>
    {text}
    <span style={{ color: "#DC2626", marginLeft: 2 }}>*</span>
  </>
);

const ProposalSummary = ({
  projectName,
  setProjectName,
  projectImage,
  setProjectImage,
  projectLocation,
  setProjectLocation,
  proposalDescription,
  setProposalDescription,
  proposalValue,
  setProposalValue,
  selectedCustomer,
  onSelectCustomer,
  onAddCustomer,
  errObj,
  setErrObj,
  timeline,
  setTimeline,
  isDisabled = false,
}) => {
  const totalWeeks = TIMELINE_KEYS.reduce(
    (acc, k) => acc + (Number(timeline?.[k]) || 0),
    0
  );
  const showTotal = Object.values(timeline || {}).some((v) => v !== "");

  const [createOpen, setCreateOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState("");

  const handleRequestCreate = (name) => {
    setCreatePrefill(name);
    setCreateOpen(true);
  };

  const handleSaveNewCustomer = (customer) => {
    onAddCustomer?.(customer);
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <SectionHeader title="Proposal Details" />
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "flex-start" },
            gap: { xs: 1.5, md: 2.5 },
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: "100%", sm: "auto" },
            }}
          >
            <ProjectImageUpload
              value={projectImage}
              onChange={setProjectImage}
              disabled={isDisabled}
              width={{ xs: "100%", sm: 200, md: 270 }}
              height={{ xs: 220, sm: 200, md: 270 }}
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: "10px",
              }}
            >
              <CInput
                label={requiredLabel("Proposal Name")}
                placeholder="Enter Proposal Name"
                value={projectName || ""}
                onChange={(e) => setProjectName?.(e.target.value)}
                disabled={isDisabled}
                required
                error={errObj?.projectNameErr}
                helpertext={errObj?.projectNameMsg}
              />
              <CInput
                label={requiredLabel("Proposal Value")}
                placeholder="Enter amount"
                type="text"
                inputMode="numeric"
                value={formatIntegerAmount(proposalValue)}
                onChange={(e) => {
                  const raw = parseIntegerAmount(e.target.value);
                  setProposalValue?.(raw);
                  if (errObj?.proposalValueErr) {
                    setErrObj?.((prev) => ({
                      ...(prev || {}),
                      proposalValueErr: false,
                      proposalValueMsg: "",
                    }));
                  }
                }}
                disabled={isDisabled}
                required
                error={errObj?.proposalValueErr}
                helpertext={errObj?.proposalValueMsg}
              />
            </Box>
            <LocationSearch
              value={projectLocation || ""}
              onChange={setProjectLocation}
              disabled={isDisabled}
            />
            {/* Scope of Work — sits inside the right column on phone (xs) and desktop (md+);
                on tablet (sm) it moves to a full-width row below the image+fields block */}
            <Box sx={{ display: { xs: "block", sm: "none", md: "block" } }}>
              <CInput
                multiline
                rows={3}
                label={requiredLabel("Scope of Work")}
                placeholder="Describe the proposed changes..."
                value={proposalDescription || ""}
                onChange={(e) => setProposalDescription?.(e.target.value)}
                disabled={isDisabled}
                required
                error={errObj?.descriptionErr}
                helpertext={errObj?.descriptionMsg}
              />
            </Box>
          </Box>
        </Box>
        {/* Tablet-only full-width Scope of Work below the image+fields block */}
        <Box sx={{ display: { xs: "none", sm: "block", md: "none" }, mt: 1.5 }}>
          <CInput
            multiline
            rows={3}
            label={requiredLabel("Scope of Work")}
            placeholder="Describe the proposed changes..."
            value={proposalDescription || ""}
            onChange={(e) => setProposalDescription?.(e.target.value)}
            disabled={isDisabled}
            required
            error={errObj?.descriptionErr}
            helpertext={errObj?.descriptionMsg}
          />
        </Box>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionHeader title="Customer Details" />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: "10px",
          }}
        >
          <CustomerSelect
            selected={selectedCustomer}
            onSelect={onSelectCustomer}
            onRequestCreate={handleRequestCreate}
            error={errObj?.customerNameErr}
            helperText={errObj?.customerNameMsg}
            disabled={isDisabled}
          />
          <CInput
            label="Customer Email"
            placeholder="Customer Email"
            value={selectedCustomer?.customer_email || ""}
            disabled
            type="email"
          />
        </Box>
      </Box>

      <Box>
        <SectionHeader title="Estimated Timeline (in weeks)" />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: "10px",
            width: "100%",
          }}
        >
          {TIMELINE_KEYS.map((key) => (
            <CInput
              key={key}
              label={key}
              type="number"
              inputProps={{ min: 0, step: 1 }}
              placeholder={TIMELINE_PLACEHOLDERS[key]}
              error={
                errObj?.timelineErr &&
                (!timeline?.[key] || String(timeline[key]).trim() === "")
              }
              helpertext={
                errObj?.timelineErr &&
                (!timeline?.[key] || String(timeline[key]).trim() === "")
                  ? errObj?.timelineMsg
                  : ""
              }
              value={timeline?.[key] || ""}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^\d+$/.test(v)) {
                  setTimeline?.((prev) => ({ ...(prev || {}), [key]: v }));
                }
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "-" ||
                  e.key === "+" ||
                  e.key === "." ||
                  e.key === "e" ||
                  e.key === "E" ||
                  e.key === "ArrowUp" ||
                  e.key === "ArrowDown"
                ) {
                  e.preventDefault();
                }
              }}
              onWheel={(e) => e.target.blur()}
              disabled={isDisabled}
            />
          ))}
        </Box>
        {showTotal && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              backgroundColor: "#F9FAFB",
              borderRadius: "8px",
              fontSize: 13,
              color: "#374151",
            }}
          >
            Total estimated duration: <strong>{totalWeeks}</strong>{" "}
            week{totalWeeks === 1 ? "" : "s"}
          </Box>
        )}
      </Box>

      <CreateCustomerModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSave={handleSaveNewCustomer}
        prefillName={createPrefill}
      />
    </Box>
  );
};

export default ProposalSummary;
